import React, { useState, useEffect, useMemo } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import {
  Container,
  CssBaseline,
  ThemeProvider,
  useMediaQuery,
} from "@mui/material";
import { supabase } from "./lib/supabaseClient";
import { lightTheme, darkTheme } from "./theme/theme";

import Login from "./components/Login";
import Register from "./components/Register";
import Dashboard from "./pages/Dashboard";
import Leaderboard from "./pages/Leaderboard";
import AuthCallback from "./pages/AuthCallback";
import VerifyPage from "./pages/Verify";
import ProfilePage from "./pages/Profile";
import Notifications from "./pages/Notifications";
import ResetPassword from "./components/resetPassword";
import AboutTeam from "./pages/AboutTeam";

import { ColorModeContext } from "./theme/ThemeContext";

function App() {
  const systemPrefersDark = useMediaQuery("(prefers-color-scheme: dark)");

  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem("themeMode");
    if (saved) return saved;
    return systemPrefersDark ? "dark" : "light";
  });

  // If we have a saved value, use it.
  // If not, follow system.
  useEffect(() => {
    if (!localStorage.getItem("themeMode")) {
      setMode(systemPrefersDark ? "dark" : "light");
    }
  }, [systemPrefersDark]);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          localStorage.setItem("themeMode", newMode);
          return newMode;
        });
      },
      mode,
    }),
    [mode]
  );

  const theme = useMemo(
    () => (mode === "dark" ? darkTheme : lightTheme),
    [mode]
  );

  const [user, setUser] = useState(null);
  const [loaded, setLoaded] = useState(false);

  // --- Guest flag helpers (supports both "guest" and "guest_mode") ---
  const readGuestFlag = () => {
    const v1 = localStorage.getItem("guest"); // "1" when guest
    const v2 = localStorage.getItem("guest_mode"); // "true" when guest
    return v1 === "1" || v2 === "true";
  };

  const clearGuestFlags = () => {
    localStorage.removeItem("guest");
    localStorage.removeItem("guest_mode");
    // notify any listeners
    window.dispatchEvent(new Event("guest-mode-change"));
  };

  const [isGuest, setIsGuest] = useState(readGuestFlag());

  useEffect(() => {
    let alive = true;

    (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!alive) return;

      setUser(session?.user ?? null);
      // if a real user is present, clear guest flags
      if (session?.user && !session.user.is_anonymous) {
        clearGuestFlags();
        setIsGuest(false);
      } else {
        setIsGuest(readGuestFlag());
      }
      setLoaded(true);
    })();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_e, session) => {
      setUser(session?.user ?? null);
      if (session?.user && !session.user.is_anonymous) {
        clearGuestFlags();
        setIsGuest(false);
      } else {
        setIsGuest(readGuestFlag());
      }
    });

    // keep isGuest in sync when Login toggles it
    const syncGuest = () => setIsGuest(readGuestFlag());
    window.addEventListener("guest-mode-change", syncGuest);
    window.addEventListener("storage", syncGuest);

    return () => {
      alive = false;
      subscription?.unsubscribe?.();
      window.removeEventListener("guest-mode-change", syncGuest);
      window.removeEventListener("storage", syncGuest);
    };
  }, []);

  // Identity flags
  const isAnonymous = !!user?.is_anonymous;
  const isRealUser = !!user && !isAnonymous;

  // Guest if either real “guest” flag or anonymous session
  const effectiveGuest = isGuest || isAnonymous;

  // Can enter app pages if real user OR guest
  const canAccessApp = isRealUser || effectiveGuest;

  if (!loaded) return null;

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <Router>
          <CssBaseline />
          <Routes>
            {/* Auth pages (public; guests allowed) */}
            <Route
              path="/login"
              element={
                <PublicRoute isRealUser={isRealUser}>
                  <Login onLogin={setUser} />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute isRealUser={isRealUser}>
                  <Register onRegister={setUser} />
                </PublicRoute>
              }
            />

            {/* Public page */}
            <Route
              path="/verify"
              element={<VerifyPage onLogout={() => setUser(null)} />}
            />
            <Route path="/about-team" element={<AboutTeam />} />

            {/* App pages (guest allowed) */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute
                  canAccessApp={canAccessApp}
                  allowGuest={true}
                  isRealUser={isRealUser}
                >
                  <Container maxWidth={false} disableGutters>
                    <Dashboard onLogout={() => setUser(null)} />
                  </Container>
                </PrivateRoute>
              }
            />
            {/* App pages (guest allowed) */}
            <Route
              path="/leaderboard"
              element={
                <PrivateRoute
                  canAccessApp={canAccessApp}
                  allowGuest={true}
                  isRealUser={isRealUser}
                >
                  <Container maxWidth={false} disableGutters>
                    <Leaderboard onLogout={() => setUser(null)} />
                  </Container>
                </PrivateRoute>
              }
            />
            {/* App pages (guest NOT allowed) */}
            <Route
              path="/profile"
              element={
                <PrivateRoute
                  canAccessApp={canAccessApp}
                  allowGuest={false}
                  isRealUser={isRealUser}
                >
                  <ProfilePage onLogout={() => setUser(null)} />
                </PrivateRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <PrivateRoute
                  canAccessApp={canAccessApp}
                  allowGuest={false}
                  isRealUser={isRealUser}
                >
                  <Container>
                    <Notifications />
                  </Container>
                </PrivateRoute>
              }
            />

            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Auth callback should always render */}
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* Default */}
            <Route path="/" element={<Navigate to="/about-team" replace />} />
          </Routes>
        </Router>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

// ► Guards
const PrivateRoute = ({
  children,
  allowGuest = true,
  canAccessApp,
  isRealUser,
}) => {
  if (!canAccessApp) return <Navigate to="/login" replace />;
  if (!allowGuest && !isRealUser) {
    // block guests from private pages like /profile, /notifications
    return <Navigate to="/register" replace />;
  }
  return children;
};

const PublicRoute = ({ children, isRealUser }) => {
  // Only block real users from auth pages; guests can still visit
  if (isRealUser) return <Navigate to="/dashboard" replace />;
  return children;
};

export default App;
